/* eslint-disable no-nested-ternary */
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as Alert } from "../../../assets/svg/alert.svg";
import {
  InstrumentTypeResponse,
  SideResponse,
} from "../../../codegen-api";
import { COLORS, TEXT_COLORS } from "../../../constants/design/colors";
import {
  INDEX_PRICE_COLLAR_OPTIONS,
  INDEX_PRICE_COLLAR_PERPS,
  MARK_PRICE_COLLAR,
} from "../../../constants/precision/form";
import { MarketInstrumentContext } from "../../../contexts/MarketInstrumentContext";
import useIndexWSS from "../../../hooks/wss/useIndexWSS";
import useMarkPriceWSS from "../../../hooks/wss/useMarkPriceWSS";
import useOrderbookWSS from "../../../hooks/wss/useOrderbookWSS";
import { maxOrderSizeForMarkPriceCollar } from "../../../utils/orderbook";
import { Button, ButtonThemeEnum } from "../../Buttons/styles";
import ModalOverlayInfo from "../ModalOverlayInfo";
import { Spinner } from "../Spinner";
import { ModalErrorMessage } from "../style";
import {
  ButtonsContainer,
  ConfirmCloseContent,
  Title,
} from "./style";
import { AccountStateEnum, AuthContext } from "../../../contexts/AuthContext";
import { ConnectWalletContext } from "../../../contexts/ConnectWalletContext";
import { IConfirmationOverlayInfoProps, LiquidityTypeEnum } from "./types";
import { computePositionInfo } from "./helpers";
import { renderContent } from "./content";

export function ClosePositionOverlayInfo({
  show,
  onClose,
  loading,
  error,
  position,
  orderbookData,
  onClosePosition,
}: IConfirmationOverlayInfoProps) {
  const { orderbook: wsOrderbook } = useOrderbookWSS(
    orderbookData ? undefined : position.instrument_name
  );
  const orderbook = wsOrderbook || orderbookData;
  const { accountSigningKeyState } = useContext(AuthContext);
  const { setShowConnectModal } = useContext(ConnectWalletContext);
  const { getMarketPrecision } = useContext(MarketInstrumentContext);
  const { t } = useTranslation("app", {
    keyPrefix: "shared.ClosePositionOverlayInfo",
  });

  const uniqueTickers = useMemo(
    () => ({
      [position.instrument_name]: {
        asset: position.asset,
        derivative: position.option
          ? InstrumentTypeResponse.Option
          : InstrumentTypeResponse.Perpetual,
      },
    }),
    [position]
  );

  const { instrumentMark } = useMarkPriceWSS(uniqueTickers);
  const mark = instrumentMark[position.instrument_name];

  const { index } = useIndexWSS(position.asset);
  const pendingCloseFundingFee = Number(
    position.pending_close_funding_fee || position.pending_funding_fee || 0
  );
  const fundingFeeDisplayValue = -pendingCloseFundingFee;

  // if buy, use bids. because closing pos will sell into the bids
  const liquidity: string[][] | undefined =
    position.side === SideResponse.Buy ? orderbook?.bids : orderbook?.asks;

  const bestLiquidity =
    (position.side === SideResponse.Buy
      ? liquidity?.sort((a, b) => Number(b[0]) - Number(a[0]))
      : liquidity?.sort((a, b) => Number(a[0]) - Number(b[0])))?.[0] || [];
  const bestLiquiditySize = Number(bestLiquidity?.[1] || 0);
  const totalLiquiditySize = Number(
    liquidity?.reduce((prev, liq) => prev + Number(liq[1]), 0)
  );

  const liquidityType: LiquidityTypeEnum =
    bestLiquiditySize === 0
      ? LiquidityTypeEnum.NONE
      : bestLiquiditySize >= Number(position.amount)
      ? LiquidityTypeEnum.SUFFICIENT
      : totalLiquiditySize >= Number(position.amount)
      ? LiquidityTypeEnum.BLEND
      : LiquidityTypeEnum.INSUFFICIENT;

  // If position is an option, detect order protection
  const maxOrderSizeBeforeOBProtectionTriggered =
    position.option &&
    (liquidityType === LiquidityTypeEnum.SUFFICIENT ||
      liquidityType === LiquidityTypeEnum.BLEND)
      ? maxOrderSizeForMarkPriceCollar(
          position.side === SideResponse.Buy
            ? SideResponse.Sell
            : SideResponse.Buy,
          orderbook?.bids || [],
          orderbook?.asks || [],
          Number(mark?.mark_price || 0),
          Number(index?.price || 0),
          MARK_PRICE_COLLAR,
          position.instrument_type === InstrumentTypeResponse.Option
            ? INDEX_PRICE_COLLAR_OPTIONS
            : INDEX_PRICE_COLLAR_PERPS
        )
      : Number.MAX_VALUE;

  const showOrderProtection =
    Number(position.amount) > maxOrderSizeBeforeOBProtectionTriggered;

  const positionInfo = computePositionInfo({
    liquidity,
    liquidityType,
    position,
    bestLiquidity,
    totalLiquiditySize,
    mark,
    getMarketPrecision,
  });

  const content = useMemo(
    () =>
      renderContent({
        pendingCloseFundingFee,
        fundingFeeDisplayValue,
        positionInfo,
        showOrderProtection,
        position,
        getMarketPrecision,
        t,
      }),
    [
      fundingFeeDisplayValue,
      pendingCloseFundingFee,
      positionInfo,
      showOrderProtection,
      position.side,
      position.asset,
      position.instrument_type,
      getMarketPrecision,
      t,
    ]
  );

  return (
    <ModalOverlayInfo
      noPadding
      hideCloseButton={!showOrderProtection}
      show={show}
      onClose={onClose}
      height="300px"
    >
      <ConfirmCloseContent>
        <div style={{ flex: 1 }}>
          {showOrderProtection ? (
            <Title $color={COLORS.negative.one}>
              {t("order_protection_triggered")} <Alert />
            </Title>
          ) : (
            <Title>{t("close_position")}?</Title>
          )}
          {content}
        </div>
        {!showOrderProtection && (
          <ButtonsContainer>
            {accountSigningKeyState !== AccountStateEnum.OK ? (
              <Button
                fullWidth
                buttonTheme={ButtonThemeEnum.HIGHLIGHT}
                onClick={() => setShowConnectModal(true)}
              >
                {t("complete_sign_in")}
              </Button>
            ) : (
              <Button
                fullWidth
                buttonTheme={ButtonThemeEnum.NEUTRAL3}
                disabled={loading}
                onClick={onClosePosition}
              >
                {loading ? (
                  <Spinner color={TEXT_COLORS.one} />
                ) : (
                  t("close_position")
                )}
              </Button>
            )}
            <Button
              fullWidth
              disabled={loading}
              buttonTheme={ButtonThemeEnum.NEGATIVE}
              onClick={onClose}
            >
              {t("cancel")}
            </Button>
            {error && <ModalErrorMessage>{error}</ModalErrorMessage>}
          </ButtonsContainer>
        )}
      </ConfirmCloseContent>
    </ModalOverlayInfo>
  );
}
